import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import {
  AdminTable, AdminSectionHeader, StatusBadge,
  tdCls, exportCsv,
} from '@/components/admin/AdminShared';

const BOOKING_STATUSES = ['pending', 'confirmed', 'cancelled'] as const;

export default function AdminBookings() {
  const { data: bookings = [], refetch } = trpc.bookings.list.useQuery();
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const updateStatus = trpc.bookings.updateStatus.useMutation({
    onSuccess: () => refetch(),
  });

  const pending   = bookings.filter(b => b.status === 'pending').length;
  const confirmed = bookings.filter(b => b.status === 'confirmed').length;

  const handleExport = () =>
    exportCsv(
      bookings.map(b => ({
        ID:           b.id,
        User:         (b as any).user?.name ?? '',
        Email:        (b as any).user?.email ?? '',
        Mobile:       b.mobileNumber,
        Session_Date: (b as any).session?.date ?? '',
        Session_Time: (b as any).session?.time ?? '',
        Topic:        b.topic,
        Message:      b.message,
        Status:       b.status,
        Booked_At:    new Date(b.createdAt).toLocaleDateString(),
      })),
      'mrpanadol_bookings'
    );

  return (
    <div>
      <AdminSectionHeader
        title="Bookings"
        count={bookings.length}
        onExport={handleExport}
      />

      {/* Quick-filter chips */}
      {bookings.length > 0 && (
        <div className="flex gap-3 mb-6 flex-wrap">
          {[
            { label: `${pending} Pending`,   color: 'bg-yellow-400/10 text-yellow-400 border-yellow-400/20' },
            { label: `${confirmed} Confirmed`, color: 'bg-green-500/10 text-green-400 border-green-500/20' },
          ].map(chip => (
            <span key={chip.label}
                  className={`text-xs font-bold px-3 py-1.5 rounded-full border ${chip.color}`}>
              {chip.label}
            </span>
          ))}
        </div>
      )}

      <AdminTable
        headers={['User', 'Session', 'Topic', 'Message', 'Mobile', 'Booked', 'Status', 'Update']}
        empty={bookings.length === 0}
      >
        {bookings.map(b => (
          <tr key={b.id} className="border-t border-white/[0.04]">

            {/* User */}
            <td className={tdCls}>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-yellow-400 shrink-0
                                flex items-center justify-center
                                text-black font-black text-xs">
                  {((b as any).user?.name?.[0] ?? '?').toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-white text-sm font-semibold truncate">
                    {(b as any).user?.name ?? '—'}
                  </p>
                  <p className="text-[#52525B] text-xs truncate">
                    {(b as any).user?.email ?? ''}
                  </p>
                </div>
              </div>
            </td>

            {/* Session date + time */}
            <td className={`${tdCls} whitespace-nowrap`}>
              <p className="text-[#E4E4E7] text-sm font-semibold">
                {(b as any).session?.date ?? '—'}
              </p>
              <p className="text-[#52525B] text-xs">
                {(b as any).session?.time?.slice(0, 5) ?? ''}
              </p>
            </td>

            {/* Topic */}
            <td className={tdCls}>
              <p className="text-[#E4E4E7] text-sm max-w-[160px] truncate">{b.topic}</p>
            </td>

            {/* Message — expandable */}
            <td className={`${tdCls} max-w-[180px]`}>
              {expandedId === b.id ? (
                <div>
                  <p className="text-[#A1A1AA] text-xs leading-relaxed whitespace-pre-wrap break-words">
                    {b.message}
                  </p>
                  <button onClick={() => setExpandedId(null)}
                          className="text-yellow-400 text-xs mt-1 hover:text-yellow-400">
                    Show less
                  </button>
                </div>
              ) : (
                <div>
                  <p className="text-[#71717A] text-xs truncate">{b.message}</p>
                  {b.message.length > 40 && (
                    <button onClick={() => setExpandedId(b.id)}
                            className="text-yellow-400 text-xs mt-0.5 hover:text-yellow-400">
                      Show more
                    </button>
                  )}
                </div>
              )}
            </td>

            {/* Mobile number */}
            <td className={`${tdCls} text-[#A1A1AA] text-xs whitespace-nowrap`}>
              <a href={`https://wa.me/${b.mobileNumber.replace(/\D/g,'')}`}
                 target="_blank" rel="noopener noreferrer"
                 className="hover:text-green-400 transition-colors flex items-center gap-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                  <path d="M12.005 2C6.486 2 2 6.486 2 12.005c0 1.76.459 3.411 1.27 4.844L2 22l5.233-1.256A9.96 9.96 0 0012.005 22C17.519 22 22 17.514 22 12.005 22 6.486 17.519 2 12.005 2zm0 18.222a8.22 8.22 0 01-4.193-1.148l-.3-.178-3.106.746.779-3.009-.197-.314A8.222 8.222 0 013.778 12c0-4.543 3.684-8.222 8.222-8.222 4.543 0 8.222 3.684 8.222 8.222 0 4.543-3.679 8.222-8.217 8.222z"/>
                </svg>
                {b.mobileNumber}
              </a>
            </td>

            {/* Booked at */}
            <td className={`${tdCls} text-[#52525B] text-xs whitespace-nowrap`}>
              {new Date(b.createdAt).toLocaleDateString()}<br />
              <span className="text-[#3F3F46]">
                {new Date(b.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </td>

            {/* Status badge */}
            <td className={tdCls}>
              <StatusBadge status={b.status} />
            </td>

            {/* Update status dropdown */}
            <td className={tdCls}>
              {b.status === 'cancelled' ? (
                <span className="text-[#3F3F46] text-xs">—</span>
              ) : (
                <select
                  value={b.status}
                  onChange={e => {
                    const newStatus = e.target.value as typeof BOOKING_STATUSES[number];
                    if (newStatus !== b.status) {
                      updateStatus.mutate({ id: b.id, status: newStatus });
                    }
                  }}
                  className="mp-card text-[#E4E4E7]
                             text-xs rounded-lg px-3 py-2 outline-none
                             focus:border-yellow-400/40 cursor-pointer"
                >
                  {BOOKING_STATUSES.filter(s =>
                    // Can't un-cancel; confirmed can only be cancelled
                    !(b.status === 'confirmed' && s === 'pending')
                  ).map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              )}
            </td>
          </tr>
        ))}
      </AdminTable>
    </div>
  );
}
